import { FormApi } from 'final-form';
import React from 'react';
import AnimateHeight from 'react-animate-height';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import ContextMenu, { ContextMenuAction } from 'src/components/common/ContextMenu';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import FormTextbox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { SelectItem } from 'src/models/common/SelectItem';
import { OwnershipCategoryType } from 'src/models/OwnershipCategory';
import Ownership from 'src/models/tract_detail/Ownership';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { IRootState } from 'src/reducers';
import AddressUtil from 'src/util/Address';
import MathUtil from 'src/util/MathUtil';
import useFetch from 'use-http';

interface ISurfaceOwnershipCardProps {
    namePrefix: string;
    formApi: FormApi;
    surfaceTractId: number;
    ownerLookupId: number;
    surfaceTractAcreage: number;
    contactCategories: Array<SelectItem>;
    ownership: Ownership;
    categoryTypes: Array<OwnershipCategoryType>;
    ownershipActions?: ContextMenuAction[];
    leaseBuyers: SelectItem[];
    isChild?: boolean;
    onOwnershipDeleted: (ownershipId: number) => void;
    openContactNotes: (surfaceOwnership: Ownership) => void;
    openOwnershipNotes: (ownership: Ownership, surfaceTractId: number) => void;
    onAddOwnership?: (parentOwnershipId: number) => void;
}

const SurfaceOwnershipCard: React.FC<ISurfaceOwnershipCardProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [contextShowing, setContextShowing] = React.useState<boolean>(false);
    // Owner name/address is now delivered inline on the ownership (ownerInfo) by the
    // surfaceTracts endpoint, so we no longer fetch /owners/{id} per card.
    const owner = props.ownership.ownerInfo;
    const [additionalHeight, setAdditionalHeight] = React.useState<string | number>(0);
    const { del, response } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    // NOTE: interest/net-acres formatting that used to happen here per-card via a
    // setTimeout(form.change) on mount is now done once, up front, when SurfaceOwnershipList
    // builds the form's initial data. Mutating the form from every card on mount caused a
    // runaway re-render loop (the form re-renders all cards on every change).

    const toggleAdditionalOwnerships = (e: unknown) => {
        const newHeight = additionalHeight === 'auto' ? 0 : 'auto';
        setAdditionalHeight(newHeight);
    };

    const additionalOwnership = () => {
        return props.ownership.additionalOwnerships.map((ao, index) => {
            const additionalOwnershipActions: ContextMenuAction[] = [
                { text: 'Delete Ownership', action: deleteOwnership },
                { text: 'Contact Notes', action: () => props.openContactNotes(props.ownership) },
            ];

            return (
                // <SurfaceOwnershipCard
                //     key={`additionalOwnership[${index}]`}
                //     ownershipActions={additionalOwnershipActions}
                //     ownership={ao}
                //     {...props}
                // />
                <SurfaceOwnershipCard
                    key={`additionalOwnerships[${index}]`}
                    formApi={props.formApi}
                    surfaceTractId={props.surfaceTractId}
                    onOwnershipDeleted={props.onOwnershipDeleted}
                    namePrefix={`${props.namePrefix}.additionalOwnerships[${index}]`}
                    ownership={ao}
                    categoryTypes={props.categoryTypes}
                    ownerLookupId={ao.ownerLookUpId}
                    ownershipActions={additionalOwnershipActions}
                    surfaceTractAcreage={props.surfaceTractAcreage}
                    contactCategories={props.contactCategories}
                    openContactNotes={props.openContactNotes}
                    openOwnershipNotes={props.openOwnershipNotes}
                    leaseBuyers={props.leaseBuyers}
                    isChild={true}
                />
            );
        });
    };

    const deleteOwnership = () => {
        const confirmAction = async () => {
            await del(`${Url.tract.horizon(appContext)}/surfaceTracts/${props.surfaceTractId}/ownerships/${props.ownership.id}`);

            if (response.ok) {
                if (props.onOwnershipDeleted) {
                    props.onOwnershipDeleted(props.ownership.id);
                }
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>Are you sure you want to delete this Surface Ownership?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Surface Ownership`, body, confirmAction);
    };

    const actions: ContextMenuAction[] = !!props.ownershipActions ? props.ownershipActions : 
    [
        { text: 'Delete Ownership', action: deleteOwnership },
        { text: 'Contact Notes', action: () => props.openContactNotes(props.ownership) },
        { text: 'Ownership Notes', action: () => props.openOwnershipNotes(props.ownership, props.surfaceTractId) },
        { text: 'Add Additional Ownership', action: () => props.onAddOwnership(props.ownership.id)},
    ];

    const changeInterest = (newInterest: number) => {
        const acreage = newInterest * props.surfaceTractAcreage;
        props.formApi.change(`${props.namePrefix}.netAcres`, MathUtil.cleanNumber(acreage));
    };

    const changeNetAcres = (newAcres: number) => {
        const interest = newAcres / props.surfaceTractAcreage;
        props.formApi.change(`${props.namePrefix}.decimalInterest`, MathUtil.cleanNumber(interest, 10, 0));
    };

    const onBlur = () => {
        const currentInterest = props.formApi.getFieldState(`${props.namePrefix}.decimalInterest`).value;
        props.formApi.change(`${props.namePrefix}.decimalInterest`, MathUtil.cleanNumber(parseFloat(currentInterest), 10, 0));
        const currentAcreage = props.formApi.getFieldState(`${props.namePrefix}.netAcres`).value;
        props.formApi.change(`${props.namePrefix}.netAcres`, MathUtil.cleanNumber(parseFloat(currentAcreage)));
        const currentCategory = props.formApi.getFieldState(`${props.namePrefix}.ownershipContactCategoryId`).value;
        props.formApi.change(`${props.namePrefix}.ownershipContactCategoryId`, currentCategory);
    };

    // TODO: Fix these so they're not hard-coded values.
    const determineOwnerType = (id: number) => {
        let type = 'Unknown';
        switch (id) {
            case 1:
                type = 'Heir';
                break;
            case 2:
                type = 'Remainderman';
                break;
            case 3:
                type = 'Non-Executive';
                break;
            case 4:
                type = 'Non-Participating';
                break;
            case 5:
                type = 'Transfer On Death';
                break;
            case 6:
                type = 'Naked Owner';
                break;
            default:
                break;
        }

        return type;
    };
    
    const ownerDescription = () => {
        if (props.isChild === true) {
            return `${owner.name} - ${determineOwnerType(props.ownership.additionalOwnershipTypeId)} ${props.ownership.npriStatus ?? ''}`;
        }
        return owner.name;
    };
    
    const handleCategoryOptions = (typeId: number) => {
        let catType = props.categoryTypes?.find(ct => ct.id === typeId);

        return catType?.selections?.map(s => new SelectItem(s.id, s.text)) ?? [];
    };

    return (
        <div onMouseEnter={() => setContextShowing(true)} onMouseLeave={() => setContextShowing(false)}>
            <div className="surface-ownership-card" >
                <div style={{ position: 'absolute', right: '-8px', top: '-2px' }}>
                    <ContextMenu actions={actions} isVisible={contextShowing} />
                </div>
                <div style={{ paddingLeft: '2.5rem', fontSize: '1.6rem' }}>
                    {owner && (
                        <>
                            {ownerDescription()}
                            <div style={{ fontSize: '1.4rem' }}>{AddressUtil.concatWithCommas(owner.addressLine1, owner.city, owner.state)}</div>
                        </>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {props.ownership.ownershipCategories?.map((oc, index) => {
                        return (
                            <div key={`ownershipCategory-${index}`} className="mr-1" style={{ width: '23rem' }}>
                                <div hidden={true}>
                                    <FormTextbox
                                        label="NOSHOW"
                                        name={`${props.namePrefix}.ownershipCategories[${index}].id`}
                                        type={FormTextBoxDataType.Int}
                                    />
                                    <FormTextbox
                                        label="NOSHOW"
                                        name={`${props.namePrefix}.ownershipCategories[${index}].ownershipId`}
                                        type={FormTextBoxDataType.Int}
                                    />
                                    <FormTextbox
                                        label="NOSHOW"
                                        name={`${props.namePrefix}.ownershipCategories[${index}].categoryTypeId`}
                                        type={FormTextBoxDataType.Int}
                                    />
                                </div>
                                <FormSelect
                                    label={oc.categoryTypeName}
                                    name={`${props.namePrefix}.ownershipCategories[${index}].categorySelectionId`}
                                    items={handleCategoryOptions(oc.categoryTypeId)}
                                    style={{ minWidth: '23rem' }}
                                />
                            </div>
                        );
                    })}
                    <div className="mr-1">
                        <FormSelect label="Contact Category" name={`${props.namePrefix}.ownershipContactCategoryId`} items={props.contactCategories} />
                    </div>
                    <div className="mr-1">
                        <FormSelect label="Lease Buyer" name={`${props.namePrefix}.leaseBuyer`} items={props.leaseBuyers} />
                    </div>
                    <div className="mr-1">
                        <FormTextbox
                            label="Interest"
                            name={`${props.namePrefix}.decimalInterest`}
                            onChange={(e) => changeInterest(parseFloat(e.currentTarget.value))}
                            onBlur={onBlur}
                            type={FormTextBoxDataType.Float}
                        />
                    </div>
                    <div>
                        <FormTextbox
                            label="Net Acres"
                            name={`${props.namePrefix}.netAcres`}
                            onChange={(e) => changeNetAcres(parseFloat(e.currentTarget.value))}
                            onBlur={onBlur}
                            type={FormTextBoxDataType.Float}
                        />
                    </div>
                </div>
            </div>
            {props.ownership?.additionalOwnerships?.length > 0 && (
                <div>
                    <div onClick={toggleAdditionalOwnerships} style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{additionalHeight === 'auto' ? '-' : '+'}</span>
                        {`Additional Ownerships (${props.ownership.additionalOwnerships.length})`}
                    </div>
                    <AnimateHeight duration={300} height={additionalHeight}>
                        {additionalOwnership()}
                    </AnimateHeight>
                </div>
            )}
        </div>
    );
};

export default SurfaceOwnershipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/SurfaceOwnershipCard.tsx